/**
 * Modal for viewing a rendered PDF page from an uploaded document.
 * Fetches page images through the authenticated API client (not raw img src)
 * to ensure auth tokens are sent in production.
 */

import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { findTenantSource, fetchPageCount, fetchSourcePageBlob } from "../../api/client";

interface Props {
  dealId: string;
  docId: string;
  filename: string;
  tenantName: string | null;
  onClose: () => void;
}

export default function SourceViewerModal({ dealId, docId, filename, tenantName, onClose }: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);
  const [tenantPages, setTenantPages] = useState<number[]>([]);

  // On mount: find which page the tenant is on, get total page count
  useEffect(() => {
    let cancelled = false;

    async function init() {
      setInitialLoading(true);
      try {
        const countPromise = fetchPageCount(dealId, docId);
        const findPromise = tenantName
          ? findTenantSource(dealId, docId, tenantName)
          : Promise.resolve(null);

        const [count, source] = await Promise.all([countPromise, findPromise]);
        if (cancelled) return;

        setTotalPages(count);
        let startPage = 1;
        if (source?.found && source.pages?.length) {
          setTenantPages(source.pages);
          startPage = source.pages[0];
        }
        setCurrentPage(startPage);
      } catch {
        // Fall through — show page 1
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [dealId, docId, tenantName]);

  // Fetch page image via authenticated API when currentPage changes
  useEffect(() => {
    if (initialLoading || totalPages === 0) return;
    let cancelled = false;

    async function loadPage() {
      setPageLoading(true);
      setImgError(false);
      // Revoke previous blob URL to avoid memory leak
      if (imgUrl) URL.revokeObjectURL(imgUrl);

      try {
        const blob = await fetchSourcePageBlob(dealId, docId, currentPage, tenantName || undefined);
        if (cancelled) return;
        setImgUrl(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) setImgError(true);
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    }

    loadPage();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, docId, currentPage, initialLoading, totalPages]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (imgUrl) URL.revokeObjectURL(imgUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOnTenantPage = tenantPages.includes(currentPage);
  const isLoading = initialLoading || pageLoading;

  const goPage = useCallback((delta: number) => {
    setCurrentPage((p) => Math.max(1, Math.min(totalPages, p + delta)));
  }, [totalPages]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-grove-200">
          <div>
            <h3 className="text-sm font-semibold text-bark-800">{filename}</h3>
            {tenantName && (
              <p className="text-xs text-bark-400 mt-0.5">
                {isOnTenantPage
                  ? `Showing source page for ${tenantName}`
                  : `${tenantName} found on page${tenantPages.length > 1 ? "s" : ""} ${tenantPages.join(", ") || "—"}`}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-grove-100 text-bark-400 hover:text-bark-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Page image */}
        <div className="flex-1 overflow-auto p-4 bg-grove-50 flex items-start justify-center min-h-[400px]">
          {isLoading ? (
            <div className="flex items-center gap-2 text-bark-400 mt-20">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">{initialLoading ? "Loading document..." : "Loading page..."}</span>
            </div>
          ) : imgError ? (
            <p className="text-bark-400 text-sm mt-20">Could not load this page.</p>
          ) : imgUrl ? (
            <img
              src={imgUrl}
              alt={`Page ${currentPage} of ${filename}`}
              className="max-w-full rounded-lg shadow-md border border-grove-300"
            />
          ) : null}
        </div>

        {/* Footer — page navigation */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-grove-200">
          <button
            onClick={() => goPage(-1)}
            disabled={currentPage <= 1 || isLoading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-bark-600 border border-grove-300 rounded-lg hover:bg-grove-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <span className="text-sm text-bark-500 tabular-nums">
            Page {currentPage} of {totalPages || "?"}
          </span>
          <button
            onClick={() => goPage(1)}
            disabled={currentPage >= totalPages || isLoading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-bark-600 border border-grove-300 rounded-lg hover:bg-grove-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
